import { Button } from "@/subframe/components/Button";
import { IconButton } from "@/subframe/components/IconButton";
import { IconWithBackground } from "@/subframe/components/IconWithBackground";
import { AppDispatch, RootState } from "../redux/store";
import { useDispatch, useSelector } from "react-redux";
import {
  addKeywordToLabel,
  Label,
  removeKeywordFromLabel,
} from "../redux/reducers/authSlice";
import { useEffect, useState } from "react";
import axios from "axios";
import { Switch } from "@/subframe/components/Switch";

import { setBrowserNotification } from "../redux/reducers/messengerSlice";
import { NotificationManager } from "../Utils/notificationHandler";

interface SettingsProps {
  isSettingOpen: boolean;
  setIsSettingOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLabelCreationOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLabelDeleteOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setLabelId: React.Dispatch<React.SetStateAction<number | undefined>>;
}

function Settings({
  isSettingOpen,
  setIsLabelCreationOpen,
  setIsSettingOpen,
  setIsLabelDeleteOpen,
  setLabelId,
}: SettingsProps) {
  const { labels } = useSelector((state: RootState) => state.auth);
  const { isBrowserNotificationOn } = useSelector(
    (state: RootState) => state.messenger
  );
  const dispatch = useDispatch<AppDispatch>();
  const [newKeyword, setNewKeyword] = useState("");
  const [isAddingKeyword, setIsAddingKeyword] = useState<number | null>(null);
  const [popupMessage, setPopupMessage] = useState<string | null>(null);
  const [popupType, setPopupType] = useState<"success" | "error" | null>(null);
  const [labelItem, setLabelItem] = useState<Label>({} as Label);
  const token = useSelector((state: RootState) => state.auth.token);

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsSettingOpen(false);
      }
    };

    if (isSettingOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; // Prevent background scroll
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isSettingOpen, setIsSettingOpen]);

  useEffect(() => {
    if (isAddingKeyword !== null) {
      const selectedLabel = labels.find(
        (label) => label.id === isAddingKeyword
      );
      if (selectedLabel) setLabelItem(selectedLabel);
    }
  }, [isAddingKeyword, labels]);

  const handleAddKeyword = async () => {
    if (!newKeyword.trim()) return;

    try {
      
      
      const response = await axios.patch(
        `https://api.insightssystem.com/api:uYdlVwac/web/label_management/${labelItem.id}`,
        {
          id: labelItem.id,
          label: labelItem.label,
          created_at: labelItem.created_at,
          keywords: [...labelItem.keywords, newKeyword],
        },
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: token })
          }
        }
      );
      dispatch(
        addKeywordToLabel({ labelId: response.data.id, newKeyword: newKeyword })
      );
      setPopupMessage("Keyword added successfully!");
      setPopupType("success");
      setNewKeyword("");
      setIsAddingKeyword(null);

      setTimeout(() => {
        setPopupMessage(null);
        setPopupType(null);
      }, 3000);
    } catch (error) {
      setPopupMessage("Failed to add keyword.");
      setPopupType("error");

      setTimeout(() => {
        setPopupMessage(null);
        setPopupType(null);
      }, 3000);
    }
  };

  const handleRemoveKeyword = async (
    labelId: number,
    keywordToRemove: string
  ) => {
    try {
      const selectedLabel = labels.find((label) => label.id === labelId);
      if (!selectedLabel) return;

      const updatedKeywords = selectedLabel.keywords.filter(
        (keyword) => keyword !== keywordToRemove
      );

      await axios.patch(
        `https://api.insightssystem.com/api:uYdlVwac/web/label_management/${labelId}`,
        {
          id: selectedLabel.id,
          label: selectedLabel.label,
          created_at: selectedLabel.created_at,
          keywords: updatedKeywords,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: token })
          }
        }
      );

      dispatch(
        removeKeywordFromLabel({
          labelId: labelId,
          removedKeyword: keywordToRemove,
        })
      );

      setPopupMessage("Keyword removed successfully!");
      setPopupType("success");

      setTimeout(() => {
        setPopupMessage(null);
        setPopupType(null);
      }, 3000);
    } catch (error) {
      setPopupMessage("Failed to remove keyword.");
      setPopupType("error");

      setTimeout(() => {
        setPopupMessage(null);
        setPopupType(null);
      }, 3000);
    }
  };

  const handleBrowserNotificationToggle = async () => {
    const newState = !isBrowserNotificationOn;

    if (newState) {
      // Trying to enable notifications
      const success = await NotificationManager.enableNotifications();
      if (success) {
        dispatch(setBrowserNotification(true));
      } else {
        // Handle permission denied case
        // You might want to show a message to the user
        alert("Notification permission denied");
      }
    } else {
      // Turning off notifications
      NotificationManager.disableNotifications();
      dispatch(setBrowserNotification(false));
    }
  };

  // Don't render if modal is not open
  if (!isSettingOpen) return null;

  return (
    <>
      {/* Modal Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
        onClick={() => setIsSettingOpen(false)}
      />
      
      {/* Modal Content */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-default-background rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col border border-solid border-neutral-border">
          {/* Modal Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-solid border-neutral-border bg-neutral-100">
            <div className="flex items-center gap-3">
              <IconButton
                variant="brand-tertiary"
                size="medium"
                icon="FeatherChevronLeft"
                onClick={() => setIsSettingOpen(false)}
                className="text-subtext-color hover:text-default-font hover:bg-neutral-200"
              />
              <h2 className="text-lg font-semibold text-default-font">Settings</h2>
            </div>
            <IconButton
              variant="brand-tertiary"
              size="medium"
              icon="FeatherX"
              onClick={() => setIsSettingOpen(false)}
              className="text-subtext-color hover:text-default-font hover:bg-neutral-200"
            />
          </div>

          {/* Modal Body */}
          <div className="flex-1 overflow-y-auto bg-default-background">
            {popupMessage && popupType && (
              <div
                className={`mx-6 mt-4 p-3 rounded-md text-white text-sm shadow-sm
                ${popupType === "success" ? "bg-green-500" : "bg-red-500"}`}
              >
                {popupMessage}
              </div>
            )}
            
            {/* Settings Sections */}
            <div className="p-6 space-y-4">
              {/* Notification Section */}
              <div className="flex items-center justify-between py-3 px-4 bg-neutral-100 rounded-lg border border-solid border-neutral-border">
                <div className="flex items-center gap-3">
                  <IconWithBackground size="small" icon="FeatherBell" />
                  <div>
                    <span className="text-sm font-medium text-default-font">Notifications</span>
                    <p className="text-xs text-subtext-color">Enable browser notifications</p>
                  </div>
                </div>
                <Switch
                  checked={isBrowserNotificationOn}
                  onCheckedChange={handleBrowserNotificationToggle}
                />
              </div>

              {/* Download Chat Section */}
              <div className="flex items-center justify-between py-3 px-4 bg-neutral-100 rounded-lg border border-solid border-neutral-border">
                <div className="flex items-center gap-3">
                  <IconWithBackground size="small" icon="FeatherBadgeInfo" />
                  <div>
                    <span className="text-sm font-medium text-default-font">Export Data</span>
                    <p className="text-xs text-subtext-color">Download chat data as CSV</p>
                  </div>
                </div>
                <IconButton
                  variant="brand-secondary"
                  icon="FeatherDownload"
                  size="small"
                  onClick={async () => {
                    try {
                      const response = await fetch('https://api.insightssystem.com/api:uYdlVwac/recipient_download_csv', {
                        headers: {
                          'Content-Type': 'application/json',
                          ...(token && { Authorization: token })
                        },
                      });
                      
                      if (response.ok) {
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'recipients.csv';
                        a.click();
                        window.URL.revokeObjectURL(url);
                        
                        setPopupMessage("Chat data downloaded successfully!");
                        setPopupType("success");
                        setTimeout(() => {
                          setPopupMessage(null);
                          setPopupType(null);
                        }, 3000);
                      } else {
                        throw new Error('Download failed');
                      }
                    } catch (error) {
                      setPopupMessage("Failed to download chat data.");
                      setPopupType("error");
                      setTimeout(() => {
                        setPopupMessage(null);
                        setPopupType(null);
                      }, 3000);
                    }
                  }}
                />
              </div>

              {/* Label Management Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <IconWithBackground size="small" icon="FeatherTag" />
                    <div>
                      <h3 className="text-sm font-medium text-default-font">Label Management</h3>
                      <p className="text-xs text-subtext-color">Organize conversations with labels and keywords</p>
                    </div>
                  </div>
                  <Button
                    className="h-8 px-4 text-sm font-medium bg-blue-600 text-white border border-blue-600 hover:bg-blue-700 hover:border-blue-700 shadow-sm transition-all duration-150 rounded-md flex items-center gap-2"
                    icon="FeatherPlus"
                    onClick={() => setIsLabelCreationOpen(true)}
                  >
                    Create Label
                  </Button>
                </div>
                
                {/* Labels Table */}
                <div className="border border-solid border-neutral-border rounded-lg overflow-hidden bg-neutral-100">
                  <div className="bg-neutral-200 border-b border-solid border-neutral-border">
                    <div className="grid grid-cols-12 gap-3 px-4 py-2">
                      <div className="col-span-3 text-xs font-semibold text-default-font uppercase tracking-wide">Label</div>
                      <div className="col-span-8 text-xs font-semibold text-default-font uppercase tracking-wide">Keywords</div>
                      <div className="col-span-1 text-xs font-semibold text-default-font uppercase tracking-wide text-center">Actions</div>
                    </div>
                  </div>
                  
                  <div className="divide-y divide-solid divide-neutral-border max-h-64 overflow-y-auto bg-default-background">
                    {labels && labels.length > 0 ? (
                      labels.map((labelItem: Label) => (
                        <div key={labelItem.id} className="px-4 py-2 hover:bg-neutral-100 transition-colors">
                          <div className="grid grid-cols-12 gap-3 items-center">
                            {/* Label Name */}
                            <div className="col-span-3">
                              <span className="text-sm font-medium text-default-font truncate block">
                                {labelItem.label}
                              </span>
                            </div>
                            
                            {/* Keywords */}
                            <div className="col-span-8">
                              <div className="flex flex-wrap items-center gap-1">
                                {labelItem.keywords.length > 0 &&
                                  labelItem.keywords.map((keyword, index) => (
                                    <div
                                      key={index}
                                      className="flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700 border border-blue-200"
                                    >
                                      <span className="truncate max-w-16">{keyword}</span>
                                      <IconButton
                                        className="h-3 w-3 hover:bg-blue-200 rounded-full flex-shrink-0"
                                        size="small"
                                        icon="FeatherX"
                                        onClick={() =>
                                          handleRemoveKeyword(labelItem.id, keyword)
                                        }
                                      />
                                    </div>
                                  ))}
                                
                                {/* Add Keyword Input and Controls */}
                                {isAddingKeyword === labelItem.id && (
                                  <div className="flex items-center gap-2 bg-default-background rounded-md px-2 py-1 border border-solid border-neutral-border shadow-sm">
                                    <input
                                      type="text"
                                      placeholder="Add keyword"
                                      value={newKeyword}
                                      onChange={(e) => setNewKeyword(e.target.value)}
                                      onKeyDown={(e) => e.key === "Enter" && handleAddKeyword()}
                                      className="px-2 py-1 text-xs border-none focus:outline-none focus:ring-0 min-w-20 bg-transparent text-default-font placeholder:text-subtext-color"
                                    />
                                    <div className="flex items-center gap-1">
                                      <IconButton
                                        className="h-4 w-4 bg-green-500 hover:bg-green-600 text-white rounded-full flex-shrink-0"
                                        size="small"
                                        icon="FeatherCheck"
                                        onClick={() => {
                                          if (!labelItem.id) return;
                                          handleAddKeyword();
                                        }}
                                      />
                                      <IconButton
                                        className="h-4 w-4 bg-red-500 hover:bg-red-600 text-white rounded-full flex-shrink-0"
                                        size="small"
                                        icon="FeatherX"
                                        onClick={() => {
                                          setIsAddingKeyword(null);
                                          setNewKeyword("");
                                        }}
                                      />
                                    </div>
                                  </div>
                                )}
                                
                                {/* Add Keyword Button */}
                                {isAddingKeyword !== labelItem.id && (
                                  <IconButton
                                    className="h-5 w-5 bg-neutral-200 hover:bg-neutral-300 text-default-font rounded-full flex-shrink-0"
                                    variant="brand-tertiary"
                                    size="small"
                                    icon="FeatherPlus"
                                    onClick={() => setIsAddingKeyword(labelItem.id)}
                                  />
                                )}
                              </div>
                            </div>
                            
                            {/* Delete Button */}
                            <div className="col-span-1 flex justify-center">
                              <IconButton
                                icon="FeatherTrash"
                                className="h-6 w-6 text-red-500 hover:bg-red-50 hover:text-red-600 rounded-md p-1"
                                onClick={() => {
                                  setLabelId(labelItem.id);
                                  setIsLabelDeleteOpen(true);
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="px-4 py-8 text-center">
                        <div className="text-subtext-color text-xs">No labels available</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Settings;
